<template>
  <div class="business-page" ref="page">
    <section class="page-title-banner" :style="{ backgroundImage: `url(${require('@/assets/images/business-title-bg.jpg')})` }">
      <div class="banner-overlay"></div>
      <div class="container">
        <span class="badge-light">BUSINESS DOMAIN</span>
        <h1 class="page-title">체계적인 시스템, 차별화된 전문성으로 <br/><span>부동산의 내일의 가치를 만듭니다</span></h1>
      </div>
    </section>

    <!-- 입주관리 로드맵 -->
    <section class="roadmap-bg container animated-section">
      <SectionHeader badge="PROCESS FLOW" title="입주 전/후 단지관리 주요업무 FLOW" :showRedLine="true" />

      <div class="roadmap-flex">
        <div class="road-step" v-for="(r, i) in roadmap" :key="i">
          <div class="step-num">STEP 0{{i+1}}</div>
          <div class="step-card">
            <div class="time">{{ r.time }}</div>
            <h4>{{ r.phase }}</h4>
            <ul>
              <li v-for="t in r.tasks" :key="t">{{ t }}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- 통합 운영 서비스 범위 -->
    <section class="service-grid-bg bg-light animated-section">
      <div class="container">
        <SectionHeader title="아스톡 통합 운영 서비스 범위" align="left" />
        <div class="s-grid">
          <a v-for="s in mainServices" :key="s.id" :href="s.link" :target="s.link ? '_blank' : '_self'" class="s-card-link">
            <div class="s-card">
              <div class="s-icon">{{ s.icon }}</div>
              <h3>{{ s.title }}</h3>
              <p>{{ s.desc }}</p>
            </div>
          </a>
        </div>
      </div>
    </section>

    <!-- 위탁관리 전문성 비교 -->
    <section class="comparison-section container animated-section">
      <SectionHeader badge="WHY PROFESSIONAL" title="위탁관리가 필요한 이유" subtitle="전문성과 투명성을 바탕으로 자산가치를 보호하고 증식합니다." />
      <div class="comparison-table">
        <div class="table-header">
          <div>구분</div>
          <div>자치관리</div>
          <div>아스톡 위탁관리</div>
        </div>
        <div class="table-row" v-for="item in managementComparison" :key="item.category">
          <div class="category">
            <span class="icon">{{ item.icon }}</span>
            {{ item.category }}
          </div>
          <div class="self">{{ item.self }}</div>
          <div class="consignment">{{ item.consignment }}</div>
        </div>
      </div>
    </section>

    <!-- 상세 서비스 소개 (탭) -->
    <section class="detailed-services-section container animated-section">
      <SectionHeader badge="DETAILED SERVICES" title="사업 영역별 상세 서비스" />
      <div class="tabs-container">
        <div class="tab-buttons">
          <button
            v-for="tab in detailedServiceTabs"
            :key="tab.id"
            :class="{ active: activeTab === tab.id }"
            @click="activeTab = tab.id"
          >
            {{ tab.title }}
          </button>
        </div>
        <div class="tab-content">
          <div v-for="tab in detailedServiceTabs" :key="tab.id" v-show="activeTab === tab.id" class="tab-pane">
            <div class="tab-image">
              <img :src="tab.image" :alt="tab.title">
            </div>
            <div class="tab-text">
              <h3>{{ tab.title }}</h3>
              <p>{{ tab.content }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 기술 컨설팅 -->
    <section class="consulting-section bg-light animated-section">
      <div class="container">
        <SectionHeader badge="ENGINEERING" title="관리 & 기술 Consulting" subtitle="건물 기획부터 사후관리까지, 건물의 가치를 높이는 솔루션을 제공합니다." />
        <div class="consulting-grid">
          <div class="consult-card" v-for="item in consultingProcess" :key="item.stage" :style="{'--card-color': item.color}">
            <div class="consult-icon">{{ item.icon }}</div>
            <div class="stage-label">{{ item.stage }}</div>
            <h4>{{ item.title }}</h4>
            <ul>
              <li v-for="task in item.tasks" :key="task">{{ task }}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- 커뮤니티 사업 -->
    <section class="community-section container animated-section">
      <SectionHeader badge="COMMUNITY" title="호텔식 커뮤니티 관리" subtitle="효율적인 시스템과 전문 노하우를 바탕으로 자산 가치를 상승시킵니다." />
      <div class="community-grid">
        <div class="comm-item" v-for="item in communityServices" :key="item.name">
          <div class="comm-icon">{{ item.icon }}</div>
          <span>{{ item.name }}</span>
        </div>
      </div>
    </section>

    <!-- 연간 관리 계획 (아코디언) -->
    <section class="annual-plan-section bg-light animated-section">
      <div class="container">
        <SectionHeader badge="ANNUAL PLAN" title="연간 시설 관리 계획" subtitle="체계적인 연간 계획을 통해 시설물의 수명을 연장하고, 안전사고를 예방합니다." />
        <div class="accordion">
          <div class="accordion-item" v-for="(item, index) in annualPlan" :key="index">
            <div class="accordion-header" @click="toggleAccordion(index)">
              <h4>{{ item.month }}</h4>
              <span class="toggle-icon">{{ activeAccordion === index ? '-' : '+' }}</span>
            </div>
            <div class="accordion-content" v-show="activeAccordion === index">
              <ul>
                <li v-for="task in item.tasks" :key="task">{{ task }}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import SectionHeader from '../components/SectionHeader.vue';
import { mainServices, managementComparison, annualPlan, detailedServiceTabs, consultingProcess, communityServices } from '../data/businessData.js';

export default {
  name: 'BusinessView',
  components: { SectionHeader },
  data() {
    return {
      roadmap: [
        { time: '입주전 3개월', phase: '준비 단계', tasks: ['초기관리계획 수립', '주요 인력 채용', '관리규약 검토'] },
        { time: '입주 기간', phase: 'Set-up 단계', tasks: ['시설물 인수인계', '법적 신고 수행', 'Key 불출 및 AS'] },
        { time: '입주후 2개월', phase: '안정화 단계', tasks: ['세대 방문 점검', '입대의 구성 지원', '커뮤니티 활성'] },
        { time: '입주후 9개월', phase: '정착 단계', tasks: ['관리비 절감 분석', '안전 실적 보고', 'TSI 만족도 조사'] }
      ],
      mainServices,
      managementComparison,
      annualPlan,
      detailedServiceTabs,
      consultingProcess,
      communityServices,
      activeAccordion: null,
      activeTab: 'facility',
    }
  },
  methods: {
    toggleAccordion(index) {
      this.activeAccordion = this.activeAccordion === index ? null : index;
    }
  },
  mounted() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in-up');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });

    this.$refs.page.querySelectorAll('.animated-section').forEach(section => {
      observer.observe(section);
    });
  }
}
</script>

<style scoped>
/* 모든 페이지의 상단 배너 스타일을 일관성 있게 정의합니다. */
.page-title-banner {
  position: relative;
  height: 350px; /* 고객문의 페이지와 동일한 높이로 설정 */
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  color: white;
  text-align: center;
}
.banner-overlay {
  background: rgba(217, 48, 37, 0.8);
}
.page-title-banner .container { position: relative; z-index: 2; }
.badge-light { background: rgba(255,255,255,0.1); color: white; padding: 8px 15px; border-radius: 20px; font-size: 13px; font-weight: 700; }
.page-title { font-size: 52px; font-weight: 900; margin-top: 25px; line-height: 1.3; }
.page-title span { color: white; text-shadow: 0 0 10px rgba(0,0,0,0.3); }

.roadmap-bg { padding: 120px 0; }
.roadmap-flex { display: flex; gap: 30px; }
.road-step { flex: 1; text-align: center; }
.road-step { flex: 1; text-align: center; position: relative; }
.road-step:not(:last-child)::after {
  content: '→';
  position: absolute;
  right: -32px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 32px;
  color: var(--primary-red);
  font-weight: bold;
}
.step-num { font-weight: 900; color: var(--primary-blue); font-size: 16px; margin-bottom: 20px; }
.step-card { padding: 30px; background: white; border-radius: 20px; box-shadow: 0 15px 40px rgba(0,0,0,0.05); border-top: 5px solid var(--primary-red); transition: all 0.3s ease; }
.road-step:hover .step-card {
  transform: translateY(-10px);
  box-shadow: 0 25px 50px rgba(0,0,0,0.08);
}
.time { color: var(--primary-red); font-weight: 900; font-size: 15px; margin-bottom: 15px; }
.step-card h4 { font-size: 22px; color: var(--primary-blue); margin-bottom: 25px; }
.step-card ul { list-style: none; padding: 0; text-align: left; }
.step-card li { font-size: 15px; color: #666; margin-bottom: 10px; padding-left: 20px; position: relative; }
.step-card li::before { content: "•"; position: absolute; left: 0; color: var(--primary-red); font-weight: bold; }

.service-grid-bg { padding: 150px 0; }
.s-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 30px; }
/* 링크 스타일 초기화 */
.s-card-link {
  text-decoration: none;
  color: inherit;
  display: block; /* 카드가 링크 영역 전체를 차지하도록 설정 */
  position: relative; /* z-index 적용을 위해 position 설정 */
}
.s-card { padding: 30px; background: white; border-radius: 20px; box-shadow: 0 10px 30px rgba(0,0,0,0.03); text-align: center; transition: 0.3s; border-top: 5px solid var(--primary-red); }
/* 호버 시 배경색만 파란색으로 변경하고, 위로 떠오르는 애니메이션 효과를 추가합니다. */
.s-card-link:hover .s-card {
  background-color: var(--primary-blue) ; /* 배경색만 파란색으로 변경 */
  color: #fff; /* 글자색 흰색 */
  transform: translateY(-10px);
  box-shadow: 0 20px 40px rgba(0,0,0,0.08);
}
.s-icon, .s-card h3, .s-card p { transition: color 0.3s ease; } /* 글자색 전환 효과 추가 */
.s-icon { font-size: 3.5rem; } /* 아이콘 크기 확대 */
.s-card h3 { font-size: 22px; margin-bottom: 20px; }
.s-card p { font-size: 15px; line-height: 1.6; opacity: 0.7; }

.comparison-section { padding: 120px 0; }
.comparison-table { border: 1px solid var(--border-color); border-radius: 15px; overflow: hidden; font-size: 16px; border-top: 5px solid var(--primary-red); }
.table-header, .table-row { display: grid; grid-template-columns: 1fr 1.5fr 1.5fr; }
.table-header { background: var(--primary-blue); color: white; font-weight: 800; }
.table-header div { padding: 20px; }
.table-row { border-top: 1px solid var(--border-color-light); transition: background-color 0.3s ease; }
.table-row:hover {
  background-color: #fdfdfe;
}
.table-row div { padding: 25px; }
.table-row .category { font-weight: 700; display: flex; align-items: center; gap: 15px; }
.table-row .icon { font-size: 24px; }
.table-row .self { color: var(--text-sub); }
.table-row .consignment { color: var(--primary-red); font-weight: 600; }

.detailed-services-section { padding: 120px 0; }
.tabs-container { }
.tab-buttons { display: flex; border-bottom: 2px solid #eee; margin-bottom: 3rem; }
.tab-buttons button {
  padding: 1rem 2rem;
  border: none;
  background: none;
  font-size: 1.1rem;
  font-weight: 700;
  cursor: pointer;
  color: var(--text-sub);
  position: relative;
  bottom: -2px;
}
.tab-buttons button.active {
  color: var(--primary-red);
  border-bottom: 2px solid var(--primary-red);
}
.tab-pane { display: grid; grid-template-columns: 1fr 1fr; gap: 4rem; align-items: center; }
.tab-image img { width: 100%; border-radius: 15px; }
.tab-text h3 { font-size: 2rem; color: var(--primary-blue); margin-bottom: 1.5rem; }
.tab-text p { font-size: 1.1rem; line-height: 1.8; color: var(--text-light); }

.consulting-section { padding: 150px 0; }
.consulting-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 3rem; }
.consult-card { padding: 4rem; border-radius: 20px; background: white; border-top: 8px solid var(--primary-red); box-shadow: 0 20px 50px rgba(0,0,0,0.07); transition: all 0.3s ease; } /* 카드 상단에 빨간색 라인 추가 */
.consult-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 30px 60px rgba(0,0,0,0.1);
}
.consult-icon { font-size: 3rem; margin-bottom: 1rem; }
.stage-label { font-weight: 800; color: var(--card-color); margin-bottom: 1rem; }
.consult-card h4 { font-size: 1.8rem; color: var(--text-dark); margin-bottom: 2rem; }
.consult-card ul { list-style: none; padding: 0; }
.consult-card li { padding-left: 1.5rem; position: relative; margin-bottom: 1rem; color: var(--text-sub); }
.consult-card li::before { content: '✓'; position: absolute; left: 0; color: var(--card-color); }

.community-section { padding: 120px 0; }
.community-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); gap: 2rem; }
.comm-item { display: flex; flex-direction: column; align-items: center; gap: 1rem; padding: 2rem; background: var(--bg-light); border-radius: 15px; transition: all 0.3s ease; border-top: 5px solid var(--primary-red); }
.comm-item:hover {
  transform: translateY(-5px);
  background: white;
  box-shadow: 0 10px 30px rgba(0,0,0,0.05);
}
.comm-icon { font-size: 2.5rem; }
.comm-item span { font-weight: 600; color: var(--text-sub); }

.annual-plan-section { padding: 150px 0; }
.accordion-item { border-bottom: 1px solid var(--border-color); }
.accordion-header { display: flex; justify-content: space-between; align-items: center; padding: 25px 0; cursor: pointer; }
.accordion-header h4 { font-size: 24px; color: var(--primary-blue); }
.toggle-icon { font-size: 30px; color: var(--primary-red); }
.accordion-content { padding: 0 20px 30px; }
.accordion-content ul { list-style-type: disc; padding-left: 20px; }
.accordion-content li { margin-bottom: 10px; color: var(--text-sub); }

@media (max-width: 1024px) {
  .roadmap-flex, .consulting-grid { flex-direction: column; }
  .s-grid { grid-template-columns: 1fr 1fr; }
  .table-header, .table-row { grid-template-columns: 1fr; }
  .table-row { border-top: 3px solid var(--border-color); }
  .table-header { display: none; }
  .tab-pane { grid-template-columns: 1fr; }
}
</style>